import { useQuery } from "@tanstack/react-query";
import { translate, translateWithVars } from "shared/languages";
import { User } from "shared/types";
import client from "shared/utils/axios-config";

/* eslint-disable react-hooks/rules-of-hooks */
// Admin add-client step 1: business-scoped lookup of a phone number.
//  - "active_member": already an active client of this studio (block)
//  - "known":         a person exists elsewhere/inactive here (confirm + add)
//  - "new":           no one with this phone (show the create form)
export interface CustomerLookupResult {
  status: "new" | "known" | "active_member";
  full_name?: string;
  // "known" only: THIS studio deactivated them; adding them reactivates it.
  deactivated_here?: boolean;
}

const getCustomerLookup = (phoneNumber: string): Promise<CustomerLookupResult> => {
  return client
    .get(`/v1/user/customer-lookup`, {
      params: { phone_number: phoneNumber },
    })
    .then((res) => res.data.data);
};

// Admin add-instructor step 1: business-scoped lookup of a phone number.
//  - "active_staff":    already active staff in this studio (block)
//  - "staff_elsewhere": staff of another studio (block; staff is single-studio)
//  - "admin_here":      an admin/manager of this studio without the
//                       instructor role (confirm + add the role; their
//                       dashboard access is untouched)
//  - "known":           a person exists but is linkable, e.g. a client or
//                       deactivated staff here (confirm + link/reactivate)
//  - "new":             no one with this phone (show the create form)
export interface StaffLookupResult {
  status: "new" | "known" | "admin_here" | "active_staff" | "staff_elsewhere";
  full_name?: string;
  // The person's current roles, on every "person exists" answer, so the
  // Karyawan modal can branch by the type being added (Instruktur vs Staf)
  // with the same rule the server enforces.
  roles?: string[];
  // "known" only: the person has a DEACTIVATED staff row here that the add
  // flow reactivates, with every role they still hold.
  deactivated_here?: boolean;
}

const getStaffLookup = (phoneNumber: string): Promise<StaffLookupResult> => {
  return client
    .get(`/v1/user/staff-lookup`, {
      params: { phone_number: phoneNumber },
    })
    .then((res) => res.data.data);
};

const useGetUserById = (userId: string, userType: string, enabled = true) => {
  return useQuery({
    queryKey: ["user", userId],
    queryFn: async (): Promise<User | undefined> => {
      const res = await client.get(`/v1/user/${userId}`, {
        params: {
          user_type: userType,
        },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
    enabled: enabled && !!userId,
  });
};

// What deactivating a client at this studio would run into (read-only; the
// deactivate PUT re-checks). Bookings that block it: Pending, Confirmed or
// Waitlist, whatever the class date. Live packages it leaves behind, with the
// co-sharers still active here who keep a shared one manageable.
export interface ClientDeactivationCheck {
  blocking_bookings: {
    id: string;
    booking_ref: string | null;
    status: string;
    schedule_id: string;
    occurrence_timestamp: string;
    waitlist_position: number | null;
    class_name: string;
  }[];
  live_packages: {
    id: string;
    name: string;
    unlimited: boolean;
    remaining_credits: number;
    expiry_date: string | null;
    active_member_names: string[];
  }[];
}

const clientDeactivationCheckOptions = (userId: string, enabled: boolean) => ({
  queryKey: ["user", userId, "deactivation-check"],
  queryFn: async (): Promise<ClientDeactivationCheck> =>
    (await client.get(`/v1/user/${userId}/deactivation-check`)).data.data,
  enabled: enabled && !!userId,
  // Asked fresh every time the pop-up opens: a booking resolved in another
  // tab must not keep blocking.
  staleTime: 0,
  gcTime: 0,
});

// What deactivating a staff member would run into (read-only; the deactivate
// PUT re-checks). Their classes that haven't started block it, one row per
// series; started classes with an unmarked booking are a reminder only.
export interface UpcomingClassRow {
  schedule_id: string;
  first_occurrence: string;
  last_occurrence: string;
  class_name: string;
  class_type: string;
  // Who teaches the row's first class (the class-delete list names them).
  instructor_name: string;
  // weekdays are Monday-first (0 = Monday); null for a single class.
  series: { freq: "daily" | "weekly" | "other"; weekdays: number[]; time: string } | null;
  class_count: number;
  booking_count: number;
}

export interface StaffDeactivationCheck {
  upcoming_classes: UpcomingClassRow[];
  totals: { classes: number; bookings: number };
  can_remove_all: boolean;
  unmarked_classes: {
    schedule_id: string;
    occurrence_timestamp: string;
    class_name: string;
    unmarked_count: number;
  }[];
  unmarked_total: number;
}

// "15 kelas mendatang" / "1 upcoming class": the counts the removal confirm
// and its toast put into a sentence (English needs the singular).
export const removalCountLabels = (classes: number, bookings: number) => ({
  classes:
    classes === 1
      ? translate("staff_status.count.class_single")
      : translateWithVars("staff_status.count.class_many", { n: classes }),
  bookings:
    bookings === 1
      ? translate("staff_status.count.booking_single")
      : translateWithVars("staff_status.count.booking_many", { n: bookings }),
});

const staffDeactivationCheckOptions = (userId: string, enabled: boolean) => ({
  queryKey: ["user", userId, "staff-deactivation-check"],
  queryFn: async (): Promise<StaffDeactivationCheck> =>
    (await client.get(`/v1/user/${userId}/staff-deactivation-check`)).data.data,
  enabled: enabled && !!userId,
  // Asked fresh every time the pop-up opens: a class moved in another tab
  // must not keep blocking.
  staleTime: 0,
  gcTime: 0,
});

export {
  clientDeactivationCheckOptions,
  staffDeactivationCheckOptions,
  getCustomerLookup,
  getStaffLookup,
  useGetUserById,
};
